import React, { useEffect, useRef, useState, useCallback } from "react";

import Head from "next/head";
import { useRouter } from "next/router";
import { useQuery } from "react-query";

import { assignInlineVars } from "@vanilla-extract/dynamic";
import { FooterData } from "data/footer";
import {
  ProductData,
  UserProductPurchaseData,
  ContentData,
  ProductProgressData,
} from "data/product/products";
import { AnimatedSpinner, Tab } from "studio-design-system";
import { useTimeout, useWindowSize } from "usehooks-ts";

import {
  BTS_PRODUCT_VIDEO_END_SESSION,
  BTS_PRODUCT_VIDEO_PAGE_LOGIN,
  BTS_PRODUCT_VIDEO_PAGE_VISIT,
  BTS_PRODUCT_VIDEO_SUCCESS_MODAL_VIEW,
} from "~/analytics/Amplitude/amplitudeConstants";
import { addUTMPropertiesToEvent } from "~/analytics/Amplitude/amplitudeUtils";
import { useAnalytics } from "~/analytics/useAnalytics";
import { getProductProgress, useUserProductPurchaseForProduct } from "~/api/productApi";
import { RateSeries } from "~/components/BTSInProductVideoPage/RateSeries";
import { ProductDetailsModal } from "~/components/BTSInProductVideoPage/modals/ProductDetailsModal";
import SuccessModal from "~/components/BTSInProductVideoPage/modals/SuccessModal/SuccessModal";
import { useAuth } from "~/hooks/auth/useAuth";
import { ReviewModalProvider } from "~/hooks/review/ReviewModalProvider";
import { Review, ReviewProvider } from "~/hooks/review/ReviewProvider";
import { useAllReviewFunctions } from "~/hooks/review/useReview";
import { useAllReviewModalFunctions } from "~/hooks/review/useReviewModal";
import { convertSecondsToMinutes } from "~/util/stringUtils";

import { LandingNavbarWithCTA } from "../common/LandingNavbarWithCTA/LandingNavbarWithCTA";
import { MarketingFooter } from "../common/MarketingFooter";
import { LandingPageSources } from "../homepage/useLandingPage";
import { VideoPlayer } from "../videoProductPage/common/VideoPlayer/VideoPlayer";
import { DEFAULT_WISTIA_OPTIONS } from "../videoProductPage/common/VideoPlayer/config";
import * as styles from "./BTSInProductVideoPage.css";
import { BTSVideoPlayer } from "./BTSVideoPlayer/BTSVideoPlayer";
import { Playlist } from "./Playlist";
import { SeriesDetails } from "./SeriesDetails";
import { SeriesTabs } from "./SeriesTabs";
import { VideoDesc } from "./VideoDesc";

interface Props {
  productData: ProductData;
  creatorDescription: string;
  footerData: FooterData;
}

const BTSInProductVideoHead = ({
  productData,
}: {
  productData: ProductData;
}): React.ReactElement => {
  return (
    <Head>
      <title>{productData.title}</title>
      <meta property="og:title" content={productData.title} />
      <meta name="description" content={productData.description} />
      <meta name="og:description" content={productData.description} />
      <meta property="og:image" content={productData.productImageURL} />
      <link rel="canonical" href={"https://studio.com"} />
    </Head>
  );
};

export const BTSInProductVideoComponent = ({
  isPlaying,
  productData,
  setIsDetailsModalOpen,
  setIsPlaying,
  shouldAutoPlay,
}: {
  productData: ProductData;
  setIsDetailsModalOpen: (isOpen: boolean) => void;
  isPlaying: boolean;
  shouldAutoPlay: boolean;
  setIsPlaying: (isPlaying: boolean) => void;
}): React.ReactElement => {
  const screen = useWindowSize();
  const router = useRouter();
  const params = router.query;
  const { isReviewModalOpen } = useAllReviewModalFunctions();
  const { currentReview, updateReviewScore } = useAllReviewFunctions();

  const { trackAmplitudeEvent } = useAnalytics();
  const [currentVideo, setCurrentVideo] = useState<ContentData>();
  const [currentVideoProgress, setCurrentVideoProgress] = useState({
    seconds: 0,
    percentage: 0,
  });
  const [errorMsg, setErrorMsg] = useState("");
  const [productProgressData, setProductProgressData] = useState<ProductProgressData>();
  const [videosWatched, setVideosWatched] = useState(isPlaying ? 1 : 0);
  const [videoIdForRating, setVideoIdForRating] = useState<string>();
  const [ratingClassView, setRatingClassView] = useState(false);
  const initialTime = Date.now();

  const hasVideoEnded =
    Math.floor(currentVideoProgress.seconds) === Math.floor(Number(currentVideo?.lengthInSeconds));

  const displayRatingView =
    !currentReview?.reviewScore &&
    !isReviewModalOpen &&
    ratingClassView &&
    hasVideoEnded &&
    convertSecondsToMinutes(currentVideo?.lengthInSeconds) >= 5 &&
    videoIdForRating === currentVideo?.id;

  const switchToNextVideo = useCallback(
    ({ isLast = false }: { isLast?: boolean }) => {
      const indexOfCurrentVideo = productData.content.findIndex((x) => x.id === currentVideo?.id);
      if (indexOfCurrentVideo !== -1) {
        const nextVideo = productData.content[indexOfCurrentVideo + 1];

        if (nextVideo) {
          setCurrentVideo(nextVideo);
          setRatingClassView(false);
          !isLast && setVideoIdForRating(nextVideo.id);
          setCurrentVideoProgress({
            seconds: 0,
            percentage: 0,
          });
        }
      }
    },
    [currentVideo, productData.content, setRatingClassView],
  );

  const handleRateAtEnd = () => {
    setVideoIdForRating(productData.content[productData.content.length - 1].id);
    switchToNextVideo({
      isLast: true,
    });
  };

  useEffect(() => {
    getProductProgress(productData.id).then((result) => setProductProgressData(result));
  }, [productData]);

  const updateCurrentVideo = (video: ContentData) => {
    setCurrentVideo(video);
    setCurrentVideoProgress({
      seconds: 0,
      percentage: 0,
    });
    if (isPlaying) setVideosWatched((videosWatched) => videosWatched + 1);
  };

  useEffect(() => {
    setVideoIdForRating(currentVideo?.id);
    if (productData.content.length == 0) {
      setErrorMsg("failed to find any videos");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onLeavePage = () => {
      trackAmplitudeEvent(BTS_PRODUCT_VIDEO_END_SESSION, {
        ...addUTMPropertiesToEvent(),
        lengthInMinutes: (Date.now() - initialTime) / (60 * 1000),
        videosWatched: videosWatched,
      });
    };

    window.addEventListener("beforeunload", onLeavePage);
    router.events.on("routeChangeStart", onLeavePage);

    return () => {
      window.removeEventListener("beforeunload", onLeavePage);
      router.events.off("routeChangeStart", onLeavePage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videosWatched]);

  const lastVideoWatched = () => {
    const foundVideo = productData.content.find(
      (content) => content.id === productProgressData?.latestEngagedContentId,
    );
    setVideoIdForRating(foundVideo ? foundVideo.id : productData.content[0].id);
    return foundVideo ? foundVideo : productData.content[0];
  };

  useEffect(() => {
    if (params?.videoId) {
      const videosFound = productData.content.filter((x) => x.id === params?.videoId);
      if (videosFound.length > 0) {
        updateCurrentVideo(videosFound[0]);
      } else {
        setErrorMsg("unable to find video!");
      }
    } else if (productData.content.length > 0 && !currentVideo && productProgressData) {
      updateCurrentVideo(lastVideoWatched());
    } else if (
      productData.content.length > 0 &&
      currentVideo &&
      !productData.content.some((obj: { id: string }) => obj.id === currentVideo.id)
    ) {
      updateCurrentVideo(lastVideoWatched());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query, productProgressData, productData, productData.id]);

  const tabs = [
    { title: "Videos", value: "videos" },
    { title: "Overview", value: "overview" },
  ];
  const [selectedTab, setSelectedTab] = useState<Tab>(tabs[0]);
  const tabletRes = 834;

  const [aspectRatioHeight, setAspectRatioHeight] = useState(0);
  const videoBoxRef = useRef<any>(null);

  useEffect(() => {
    setAspectRatioHeight(((videoBoxRef.current?.clientWidth || 500) / 16) * 9);
    setRatingClassView(true);
  }, [screen.width, currentVideo]);

  useEffect(() => {
    if (
      hasVideoEnded &&
      !isReviewModalOpen &&
      !displayRatingView &&
      productData.content.findIndex((x) => x.id === currentVideo?.id) !==
      productData.content.length - 1
    ) {
      const timeout = setTimeout(() => {
        switchToNextVideo({ isLast: false });
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [
    currentVideo,
    displayRatingView,
    hasVideoEnded,
    isReviewModalOpen,
    productData.content,
    switchToNextVideo,
  ]);

  const indexOfCurrentVideo = () => {
    if (currentVideo) {
      return productData.content.findIndex((x) => x.id === currentVideo.id);
    }
    return -1;
  };


  return (
    <>
      {currentVideo && productProgressData ? (
        <div className={styles.container}>
          <div className={styles.videoWrap}>
            <div className={styles.videoBox} ref={videoBoxRef}>
              {displayRatingView ? (
                <RateSeries
                  productId={productData.id}
                  handleRateAtEnd={handleRateAtEnd}
                  updateReviewScore={updateReviewScore}
                  productReviewScore={currentReview?.reviewScore || 0}
                  handleRateLater={() => switchToNextVideo({ isLast: false })}
                  isLastVideo={indexOfCurrentVideo() === productData.content.length - 1}
                />
              ) : (
                <BTSVideoPlayer
                  url={currentVideo.mediaUrl}
                  height={aspectRatioHeight}
                  config={{ wistia: DEFAULT_WISTIA_OPTIONS }}
                  videoId={currentVideo.id}
                  productId={productData.id}
                  shouldAutoPlay={shouldAutoPlay}
                  playing={isPlaying}
                  setPlaylistProgress={(progress: number) => {
                    setCurrentVideoProgress({
                      percentage: progress,
                      seconds: Number((currentVideo.lengthInSeconds * progress).toFixed(2)),
                    });
                  }}
                  setIsPlayingHandler={
                    isReviewModalOpen ? () => { } : (isPlaying: boolean) => setIsPlaying(isPlaying)
                  }
                />
              )}
            </div>
            {screen.width <= tabletRes && (
              <SeriesTabs tabs={tabs} selection={selectedTab} setSelection={setSelectedTab} />
            )}
            <div
              className={styles.videoContentBox}
              style={assignInlineVars({
                [styles.videoBoxHeight]: `${aspectRatioHeight}px`,
              })}
            >
              {(selectedTab.value == "overview" || screen.width > tabletRes) && (
                <SeriesDetails
                  productData={productData}
                  showDescription={screen.width > tabletRes ? false : true}
                  setIsDetailsModalOpen={setIsDetailsModalOpen}
                  setIsPlaying={setIsPlaying}
                />
              )}

              {(selectedTab.value == "videos" || screen.width > tabletRes) && (
                <>
                  {screen.width <= tabletRes && (
                    <VideoDesc
                      indexOfCurrentVideo={indexOfCurrentVideo()}
                      currentVideo={currentVideo}
                    />
                  )}
                  <Playlist
                    productData={productData}
                    currentVideo={currentVideo}
                    currentVideoProgress={currentVideoProgress.percentage}
                    setCurrentVideo={updateCurrentVideo}
                    showOverlay={true}
                  />
                </>
              )}
            </div>
          </div>

          {screen.width > tabletRes && (
            <div className={styles.videoBox}>
              <VideoDesc currentVideo={currentVideo} indexOfCurrentVideo={indexOfCurrentVideo()} />
            </div>
          )}
        </div>
      ) : (
        <div className={styles.loadingContainer}>
          {errorMsg != "" ? <>Oops!, {errorMsg}</> : <AnimatedSpinner />}
        </div>
      )}
    </>
  );
};

export const BTSInProductVideo = ({
  productData,
  creatorDescription,
  footerData,
}: Props): React.ReactElement => {
  const { trackAmplitudeEvent } = useAnalytics();
  const router = useRouter();
  const params = router.query;

  const [isPlaying, setIsPlaying] = useState(false);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(true);

  const exitSuccessModal = (playImmediately = false) => {
    setIsSuccessModalOpen(false);
    if (playImmediately) {
      setIsPlaying(true);
    }
    trackAmplitudeEvent(BTS_PRODUCT_VIDEO_SUCCESS_MODAL_VIEW, {
      ...addUTMPropertiesToEvent(),
      startedWatching: playImmediately,
    });
  };

  useEffect(() => {
    trackAmplitudeEvent(BTS_PRODUCT_VIDEO_PAGE_VISIT, {
      ...addUTMPropertiesToEvent(),
      productId: productData.id,
      productTitle: productData.title,
      storefrontSlug: productData.storefrontSlug,
      creatorName: productData.creatorName,
      viewedSuccessModal: params.purchased,
    });
    if (!params.purchased) {
      setShouldAutoPlay(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isLoading, userProductPurchase } = useUserProductPurchaseForProduct(productData.id);



  return (
    <ReviewProvider>
      <ReviewModalProvider>
        {params.purchased && (
          <SuccessModal
            descriptor={"Class"}
            creatorName={productData.creatorName}
            isSuccessModalOpen={isSuccessModalOpen}
            exitSuccessModal={exitSuccessModal}
          />
        )}
        <div className={styles.outerContainer}>
          <LandingNavbarWithCTA
            onSignInEvent={() => {
              trackAmplitudeEvent(BTS_PRODUCT_VIDEO_PAGE_LOGIN, addUTMPropertiesToEvent());
            }}
            source={LandingPageSources.BTSVideo}
          />
          <BTSInProductVideoComponent
            setIsDetailsModalOpen={setIsDetailsModalOpen}
            productData={productData}
            isPlaying={isPlaying}
            setIsPlaying={setIsPlaying}
            shouldAutoPlay={shouldAutoPlay}
          />
          <MarketingFooter data={footerData} />
        </div>
        <ProductDetailsModal
          productData={productData}
          creatorDescription={creatorDescription}
          isDetailsModalOpen={isDetailsModalOpen}
          closeDetailsModal={() => setIsDetailsModalOpen(false)}
        />
      </ReviewModalProvider>
    </ReviewProvider>
  );
};
